import { cn } from 'cn';

/**
 * Flags are words, never icons alone (docs/ux/shell.md). Prior DNA is the
 * clinic's: a DNA on an earlier cycle of the case, carried on the clinic rows
 * as a door cue (docs/ux/clinic.md); the pipeline's rows never carry it.
 */
export type FlagKind = 'urgent' | 'repeatDna' | 'lateCancellation' | 'priorDna' | 'skippedAppointment' | 'dnaFeeSuggested';

const FLAGS: Record<FlagKind, { label: string; tone: 'error' | 'muted' }> = {
  urgent: { label: 'Urgent', tone: 'error' },
  repeatDna: { label: 'Repeat DNA', tone: 'error' },
  lateCancellation: { label: 'Late cancellation', tone: 'error' },
  priorDna: { label: 'Prior DNA', tone: 'error' },
  dnaFeeSuggested: { label: 'DNA fee suggested', tone: 'muted' },
  skippedAppointment: { label: 'No appointment', tone: 'muted' },
};

export function flagLabel(kind: FlagKind): string {
  return FLAGS[kind].label;
}

/**
 * A flag word. The error-tone flags (Urgent, Repeat DNA, Late cancellation,
 * Prior DNA) are filled pills in the error token with paper text (Beam, 10 September
 * 2026, D-0066: more saturation; the same colour token, no new one); the
 * muted flags stay as words.
 */
export function FlagText({ kind, className }: { kind: FlagKind; className?: string }) {
  const f = FLAGS[kind];
  return (
    <span data-slot="flag" data-flag={kind} className={cn('whitespace-nowrap text-xs', f.tone === 'error' ? 'rounded-full bg-beam-error px-2 py-0.5 font-semibold leading-4 text-beam-paper' : 'font-medium text-beam-text-muted', className)}>
      {f.label}
    </span>
  );
}

/** The flags present on a matter, in the order the pipeline shows them. */
export function activeFlags(flags: Partial<Record<FlagKind, boolean | undefined>>): FlagKind[] {
  return (Object.keys(FLAGS) as FlagKind[]).filter((k) => flags[k]);
}
